import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Download, Upload, Search, X, Building2, User, Calendar, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEnquiries, useBranches, useTeamMembers, useCustomers } from '../hooks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pagination options
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const EnquiryList = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [createdByFilter, setCreatedByFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Date filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Comments preview state
  const [expandedComments, setExpandedComments] = useState({});
  const [commentsCache, setCommentsCache] = useState({});
  
  const fileInputRef = useRef(null);
  const customerInputRef = useRef(null);
  const listContainerRef = useRef(null);
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use custom hooks for data fetching
  const { enquiries, loading, refetch: refetchEnquiries } = useEnquiries();
  const { branches } = useBranches();
  const { teamMembers, getFilteredByBranch } = useTeamMembers();
  const { customers, searchCustomers } = useCustomers();

  // Initialize filters from URL params
  useEffect(() => {
    const page = searchParams.get('page');
    const branch = searchParams.get('branch');
    const status = searchParams.get('status');
    const assignedTo = searchParams.get('assignedTo');
    const createdBy = searchParams.get('createdBy');
    const from = searchParams.get('fromDate');
    const to = searchParams.get('toDate');
    const query = searchParams.get('q');
    
    if (page) setCurrentPage(parseInt(page, 10));
    if (branch) setBranchFilter(branch);
    if (status) setStatusFilter(status);
    if (assignedTo) setAssignedToFilter(assignedTo);
    if (createdBy) setCreatedByFilter(createdBy);
    if (from) setFromDate(from);
    if (to) setToDate(to);
    if (query) setSearchQuery(query);
    
    // Restore scroll position
    const savedScroll = sessionStorage.getItem('enquiryListScroll');
    if (savedScroll) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll, 10));
        sessionStorage.removeItem('enquiryListScroll');
      }, 100);
    }
  }, []); // Run once on mount
  
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    product_name: '',
    price_details: '',
    priority: 'medium',
    status: 'new',
    follow_up_date: '',
    remarks: ''
  });

  const filterEnquiries = useCallback(() => {
    let filtered = [...enquiries];
    
    // Apply branch filter
    if (branchFilter && branchFilter !== 'all') {
      filtered = filtered.filter(enquiry => enquiry.branch_id === branchFilter);
    }
    
    // Apply created by filter
    if (createdByFilter && createdByFilter !== 'all') {
      filtered = filtered.filter(enquiry => enquiry.created_by === createdByFilter);
    }
    
    // Apply assigned to filter
    if (assignedToFilter && assignedToFilter !== 'all') {
      if (assignedToFilter === 'unassigned') {
        filtered = filtered.filter(enquiry => !enquiry.assigned_to);
      } else {
        filtered = filtered.filter(enquiry => enquiry.assigned_to === assignedToFilter);
      }
    }
    
    // Apply status filter
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered = filtered.filter(enquiry => !['won', 'lost'].includes(enquiry.status));
      } else {
        filtered = filtered.filter(enquiry => enquiry.status === statusFilter);
      }
    }
    
    // Apply date range filter (client-side for already loaded data)
    if (fromDate) {
      filtered = filtered.filter(enquiry => {
        const enquiryDate = enquiry.created_at?.substring(0, 10);
        return enquiryDate >= fromDate;
      });
    }
    if (toDate) {
      filtered = filtered.filter(enquiry => {
        const enquiryDate = enquiry.created_at?.substring(0, 10);
        return enquiryDate <= toDate;
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(enquiry => {
        const customer = customers.find(c => c.id === enquiry.customer_id);
        return (
          enquiry.customer_name?.toLowerCase().includes(query) ||
          enquiry.product_name?.toLowerCase().includes(query) ||
          customer?.phone?.toLowerCase().includes(query) ||
          customer?.email?.toLowerCase().includes(query)
        );
      });
    }
    
    setFilteredEnquiries(filtered);
    // Reset to page 1 when filters change
    if (currentPage > Math.ceil(filtered.length / pageSize)) {
      setCurrentPage(1);
    }
  }, [enquiries, branchFilter, createdByFilter, assignedToFilter, statusFilter, fromDate, toDate, searchQuery, customers, currentPage, pageSize]);

  useEffect(() => {
    filterEnquiries();
  }, [filterEnquiries]);

  const clearFilters = () => {
    setSearchQuery('');
    setBranchFilter('all');
    setCreatedByFilter('all');
    setAssignedToFilter('all');
    setStatusFilter('all');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || branchFilter !== 'all' || createdByFilter !== 'all' || assignedToFilter !== 'all' || statusFilter !== 'all' || fromDate || toDate;

  // Save scroll position before navigating to detail
  const handleEnquiryClick = (enquiryId) => {
    sessionStorage.setItem('enquiryListScroll', window.scrollY.toString());
    // URL params are already synced via syncFiltersToUrl
  };

  // Sync filters to URL params whenever they change
  const syncFiltersToUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (branchFilter !== 'all') params.set('branch', branchFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (assignedToFilter !== 'all') params.set('assignedTo', assignedToFilter);
    if (createdByFilter !== 'all') params.set('createdBy', createdByFilter);
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    if (searchQuery) params.set('q', searchQuery);
    
    setSearchParams(params, { replace: true });
  }, [currentPage, branchFilter, statusFilter, assignedToFilter, createdByFilter, fromDate, toDate, searchQuery, setSearchParams]);

  // Sync filters to URL when they change (debounced for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      syncFiltersToUrl();
    }, 300);
    return () => clearTimeout(timer);
  }, [syncFiltersToUrl]);

  // Fetch comments for a specific enquiry
  const fetchComments = async (enquiryId) => {
    if (commentsCache[enquiryId]) {
      return commentsCache[enquiryId];
    }
    try {
      const response = await axios.get(`${API}/enquiries/${enquiryId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const comments = response.data;
      setCommentsCache(prev => ({ ...prev, [enquiryId]: comments }));
      return comments;
    } catch (error) {
      return [];
    }
  };

  // Toggle comments visibility
  const toggleComments = async (e, enquiryId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!expandedComments[enquiryId]) {
      await fetchComments(enquiryId);
    }
    setExpandedComments(prev => ({
      ...prev,
      [enquiryId]: !prev[enquiryId]
    }));
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredEnquiries.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedEnquiries = filteredEnquiries.slice(startIndex, endIndex);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (submitting) return;
    
    setSubmitting(true);
    try {
      const submitData = {
        customer_id: formData.customer_id,
        product_name: formData.product_name,
        price_details: formData.price_details,
        priority: formData.priority,
        status: formData.status,
        follow_up_date: formData.follow_up_date,
        remarks: formData.remarks
      };
      await axios.post(`${API}/enquiries`, submitData);
      toast.success('Enquiry created successfully');
      setDialogOpen(false);
      setFormData({
        customer_id: '',
        customer_name: '',
        product_name: '',
        price_details: '',
        priority: 'medium',
        status: 'new',
        follow_up_date: '',
        remarks: ''
      });
      refetchEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/enquiries/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `enquiries_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Enquiries exported successfully');
    } catch (error) {
      toast.error('Failed to export enquiries');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (importing) return;
    setImporting(true);
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/enquiries/import`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      setImportDialogOpen(false);
      refetchEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import enquiries');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      quoted: 'bg-purple-100 text-purple-800',
      negotiation: 'bg-orange-100 text-orange-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-slate-100 text-slate-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const filteredCustomers = customerSearch.length >= 2 
    ? customers.filter(c => 
        c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.phone?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.email?.toLowerCase().includes(customerSearch.toLowerCase())
      ).slice(0, 10)
    : [];

  const selectCustomer = (customer) => {
    setFormData({ ...formData, customer_id: customer.id, customer_name: customer.name });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <main className="md:ml-64 min-h-screen">
        <Navbar onMenuClick={() => setSidebarOpen(true)} />
        
        <div className="p-4 md:p-6" ref={listContainerRef}>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sales Enquiries</h1>
              <p className="text-slate-600">Manage your sales pipeline and track enquiries</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-enquiries-btn">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              
              <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="import-enquiries-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    Import
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Import Enquiries from Excel</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Upload an Excel file with columns: Product Name, Customer (name or email), Price Details, Status, Priority, Follow-up Date, Remarks
                    </p>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleImport}
                      disabled={importing}
                    />
                    {importing && <p className="text-sm text-slate-500">Importing...</p>}
                  </div>
                </DialogContent>
              </Dialog>
              
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="create-enquiry-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Enquiry
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Enquiry</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="relative">
                      <Label htmlFor="customer_search">Customer *</Label>
                      <Input
                        ref={customerInputRef}
                        id="customer_search"
                        value={customerSearch}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setShowCustomerDropdown(true);
                          if (!e.target.value) {
                            setFormData({ ...formData, customer_id: '', customer_name: '' });
                          }
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        placeholder="Search customer by name, phone, or email..."
                        required
                      />
                      {showCustomerDropdown && filteredCustomers.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {filteredCustomers.map(customer => (
                            <div
                              key={customer.id}
                              className="px-3 py-2 cursor-pointer hover:bg-slate-100"
                              onClick={() => selectCustomer(customer)}
                            >
                              <p className="font-medium">{customer.name}</p>
                              <p className="text-xs text-slate-500">{customer.phone} • {customer.email}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {formData.customer_name && (
                        <p className="text-xs text-green-600 mt-1">Selected: {formData.customer_name}</p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="product_name">Product Name *</Label>
                      <Input
                        id="product_name"
                        value={formData.product_name}
                        onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                        placeholder="Enter product name"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="price_details">Price Details</Label>
                      <Textarea
                        id="price_details"
                        value={formData.price_details}
                        onChange={(e) => setFormData({ ...formData, price_details: e.target.value })}
                        placeholder="Enter pricing information"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Priority</Label>
                        <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="quoted">Quoted</SelectItem>
                            <SelectItem value="negotiation">Negotiation</SelectItem>
                            <SelectItem value="won">Won</SelectItem>
                            <SelectItem value="lost">Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="follow_up_date">Follow-up Date</Label>
                      <Input
                        id="follow_up_date"
                        type="date"
                        value={formData.follow_up_date}
                        onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="remarks">Remarks</Label>
                      <Textarea
                        id="remarks"
                        value={formData.remarks}
                        onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                        placeholder="Additional notes or remarks"
                      />
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? 'Creating...' : 'Create Enquiry'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search enquiries..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-enquiries"
                />
              </div>
              
              {/* From Date */}
              <div>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  placeholder="From Date"
                  className="w-full"
                  data-testid="from-date-filter"
                />
              </div>
              
              {/* To Date */}
              <div>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  placeholder="To Date"
                  className="w-full"
                  data-testid="to-date-filter"
                />
              </div>
              
              {/* Branch Filter */}
              {user?.role === 'admin' && (
                <Select value={branchFilter} onValueChange={setBranchFilter}>
                  <SelectTrigger data-testid="branch-filter">
                    <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active (Not Won/Lost)</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Assigned To Filter */}
              <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
                <SelectTrigger data-testid="assigned-filter">
                  <User className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Assigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assigned</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {hasActiveFilters && (
              <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Found {filteredEnquiries.length} enquir{filteredEnquiries.length !== 1 ? 'ies' : 'y'}
                </p>
                <button
                  onClick={clearFilters}
                  className="text-sm text-accent hover:underline flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {/* Pagination Controls - Top */}
          {filteredEnquiries.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Show</span>
                <Select value={pageSize.toString()} onValueChange={(val) => { setPageSize(parseInt(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-20" data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-600">per page</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredEnquiries.length)} of {filteredEnquiries.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredEnquiries.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <p className="text-slate-600">
                {hasActiveFilters ? 'No enquiries match your search/filter criteria.' : 'No enquiries yet. Create your first enquiry to get started.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedEnquiries.map((enquiry) => (
                <div key={enquiry.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <Link
                    to={`/enquiries/${enquiry.id}`}
                    onClick={() => handleEnquiryClick(enquiry.id)}
                    className="block p-6 card-hover"
                    data-testid={`enquiry-card-${enquiry.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(enquiry.status)}`}>
                        {enquiry.status.replace('_', ' ')}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(enquiry.priority)}`}>
                        {enquiry.priority}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{enquiry.product_name}</h3>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">{enquiry.price_details || 'Price not specified'}</p>
                    <div className="text-xs text-slate-500">
                      <p>Customer: {enquiry.customer_name || 'Unknown'}</p>
                      {enquiry.assigned_to_name && <p>Assigned: {enquiry.assigned_to_name}</p>}
                      {enquiry.created_by_name && <p>Created By: {enquiry.created_by_name}</p>}
                      {enquiry.follow_up_date && (
                        <p className="flex items-center gap-1 text-orange-600 font-medium mt-1">
                          <Calendar className="h-3 w-3" />
                          Follow Up: {new Date(enquiry.follow_up_date).toLocaleDateString()}
                        </p>
                      )}
                      <p>Created: {new Date(enquiry.created_at).toLocaleDateString()}</p>
                    </div>
                  </Link>
                  
                  {/* Comments Preview Toggle */}
                  <div className="border-t px-4 py-2">
                    <button
                      onClick={(e) => toggleComments(e, enquiry.id)}
                      className="flex items-center gap-2 text-sm text-slate-600 hover:text-primary transition-colors w-full"
                      data-testid={`toggle-comments-${enquiry.id}`}
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span>{expandedComments[enquiry.id] ? 'Hide Comments' : 'Show Comments'}</span>
                    </button>
                    
                    {expandedComments[enquiry.id] && (
                      <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                        {commentsCache[enquiry.id]?.length > 0 ? (
                          commentsCache[enquiry.id].map((comment, idx) => (
                            <div key={comment.id || idx} className="bg-slate-50 rounded p-2 text-xs">
                              <p className="font-medium text-slate-700">{comment.created_by_name}</p>
                              <p className="text-slate-600">{comment.text}</p>
                              <p className="text-slate-400 mt-1">{new Date(comment.created_at).toLocaleString()}</p>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-slate-400 italic">No comments yet</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Controls - Bottom */}
          {filteredEnquiries.length > pageSize && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="flex items-center px-4 text-sm text-slate-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
